import { useEffect, useMemo, useRef, useState } from 'react';
import type { Lang } from '@/data/categories';
import { categories, MAIN_SITE_URL } from '@/data/categories';
import { homeContent } from '@/data/home';
import { uiContent } from '@/data/ui';

interface Props {
  lang: Lang;
  search: string;
}

export default function CategoryHub({ lang, search }: Props) {
  const c = homeContent[lang];
  const t = uiContent[lang];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((cat) => cat.name[lang].toLowerCase().includes(q));
  }, [search, lang]);

  return (
    <section id="categories" className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{c.categoryHub.h2}</h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">{c.categoryHub.sub}</p>
      </div>

      <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
        <div ref={ref} className="relative w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-3 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition hover:border-primary-300 sm:w-72"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-lg text-primary-600"></i>
              {t.categoryLabel}
            </span>
            <i className={`ri-arrow-down-s-line text-lg transition ${open ? 'rotate-180' : ''}`}></i>
          </button>

          {open && (
            <div className="absolute left-0 right-0 z-30 mt-2 max-h-96 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 transition hover:bg-background-100"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                    <i className={`${cat.icon} text-base`}></i>
                  </span>
                  <span className="whitespace-nowrap">{cat.name[lang]}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        <a
          href={MAIN_SITE_URL}
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 sm:w-auto"
        >
          {t.mainCta}
          <i className="ri-arrow-right-up-line text-base"></i>
        </a>
        <a
          href={MAIN_SITE_URL}
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-accent-600 sm:w-auto"
        >
          {t.buyNow}
          <i className="ri-shopping-cart-line text-base"></i>
        </a>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {filtered.map((cat) => (
          <a
            key={cat.slug}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:-translate-y-1"
          >
            <div className="relative h-32 w-full overflow-hidden">
              <img
                src={cat.image}
                alt={`${cat.name[lang]} - AllChinaBuy Spreadsheet`}
                title={`${cat.name[lang]} - AllChinaBuy Spreadsheet`}
                className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex items-center gap-2 p-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className={`${cat.icon} text-sm`}></i>
              </span>
              <span className="truncate text-sm font-medium text-foreground-800">{cat.name[lang]}</span>
            </div>
          </a>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="mt-8 text-center text-sm text-foreground-500">{c.hero.search}</p>
      )}
    </section>
  );
}